import React from 'react';

import type {
  AuthMessageProps,
  AuthMessageScale,
  AuthMessageSize,
} from './AuthMessage.types';

import { Medallion, SectionLabel, Text } from '@/components/ui';

/** The title's size, the lede's, and where they sit. */
const SIZES: Record<AuthMessageSize, AuthMessageScale> = {
  md: { title: 'text-heading-md', lede: 'body-md', align: '' },
  lg: {
    title: 'text-heading-lg',
    lede: 'body-sm',
    align: 'text-center lg:text-left',
  },
};

/**
 * What an auth card leads with: the medal when it answers, the eyebrow (after
 * the card's step in a flow, if it's one of several), the title and the lede,
 * then the rest of the card. The title takes focus from code (`headingRef`),
 * so a page can move focus to it when the answer changes; `Heading` can't be
 * given a ref. The log-in and sign-up cards lead with the larger `lg` head.
 */
export function AuthMessage({
  icon,
  tone = 'neutral',
  step,
  eyebrow,
  title,
  lede,
  size = 'md',
  headingRef,
  children,
}: AuthMessageProps): React.JSX.Element {
  const { title: titleSize, lede: ledeVariant, align } = SIZES[size];

  return (
    <div>
      {icon ? (
        <Medallion
          icon={icon}
          size="lg"
          shape="circle"
          tone={tone}
          className="mb-6"
        />
      ) : null}
      {eyebrow ? (
        <SectionLabel className="mb-3" step={step}>
          {eyebrow}
        </SectionLabel>
      ) : null}
      <h1
        ref={headingRef}
        tabIndex={-1}
        className={`mb-2 font-display ${titleSize} font-bold text-neutral-50 focus:outline-none ${align}`.trim()}
      >
        {title}
      </h1>
      <Text
        variant={ledeVariant}
        color="secondary"
        className={`mb-6 ${align}`.trim()}
      >
        {lede}
      </Text>
      {children}
    </div>
  );
}

export default AuthMessage;
